import { useRef } from 'react';
import { Avatar, Dialog, Form, Input, MessagePlugin, Radio } from 'tdesign-react';
import type { FormInstanceFunctions, FormRule, SubmitContext } from 'tdesign-react';
import { UserIcon } from 'tdesign-icons-react';
import {
  AccountStatus,
  getAvatarUrl,
  getNickname,
  type Account,
} from '@/services/account';

const { FormItem } = Form;
const RadioGroup = Radio.Group;

/**
 * 编辑账号表单值
 *
 * 后台「账号管理」编辑弹窗一站式承载：
 *  - 资料补全（姓名 / 所属单位 / 手机号）
 *  - 状态变更（启用 / 禁用 / 保持待启用）
 *
 * 只读字段：
 *  - openid / unionid / extra(nickname) 由微信扫码登录时自动落库，禁止手工改动
 *  - 不保留"微信账号"字段（snsapi_login 无法获取）
 */
export interface AccountFormValue {
  name: string;
  organization: string;
  phone?: string;
  status: AccountStatus;
}

interface AccountFormDialogProps {
  visible: boolean;
  loading?: boolean;
  initialValue?: Account | null;
  onCancel: () => void;
  onSubmit: (payload: AccountFormValue) => Promise<void> | void;
}

/**
 * 表单校验规则
 *
 * 与后端 UpdateAccountDto 对齐：name / organization 必填；phone 选填但填了要合法。
 */
const rules: Record<keyof AccountFormValue, FormRule[]> = {
  name: [
    { required: true, message: '请输入姓名', type: 'error' },
    { max: 64, message: '姓名最多 64 字', type: 'warning' },
  ],
  organization: [
    { required: true, message: '请输入所属单位', type: 'error' },
    { max: 128, message: '单位最多 128 字', type: 'warning' },
  ],
  phone: [
    { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', type: 'error' },
  ],
  status: [{ required: true, message: '请选择状态', type: 'error' }],
};

/**
 * 归一化提交值
 *
 * 姓名、所属单位由 required 保证非空；此处只做 trim。
 * 手机号空串转 undefined，避免后端把空串当"清空"语义。
 */
const normalize = (v: AccountFormValue): AccountFormValue => ({
  name: v.name.trim(),
  organization: v.organization.trim(),
  phone: v.phone?.trim() || undefined,
  status: v.status,
});

const toFormValue = (record?: Account | null): AccountFormValue => ({
  name: record?.name || '',
  organization: record?.organization || '',
  phone: record?.phone || '',
  // 新建/未知记录默认待启用，避免 required 校验误报
  status: record?.status ?? AccountStatus.PENDING,
});

const AccountFormDialog = ({
  visible,
  loading,
  initialValue,
  onCancel,
  onSubmit,
}: AccountFormDialogProps) => {
  const formRef = useRef<FormInstanceFunctions>(null);

  const handleSubmit = async (ctx: SubmitContext) => {
    if (ctx.validateResult !== true) return;
    const values = formRef.current?.getFieldsValue?.(true) as AccountFormValue | undefined;
    if (!values) return;
    try {
      await onSubmit(normalize(values));
    } catch (err) {
      const msg = err instanceof Error ? err.message : '提交失败';
      MessagePlugin.error(msg);
    }
  };

  const handleConfirm = () => {
    formRef.current?.submit?.();
  };

  return (
    <Dialog
      header="编辑账号"
      visible={visible}
      onClose={onCancel}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      confirmLoading={loading}
      destroyOnClose
      width={560}
    >
      {/*
        账号只读信息头（不进 Form）：头像 + 昵称 + OpenID
        —— 这里刻意不用 <FormItem>，因为 TDesign 的 Form 会接管其内 Input 的 value；
           而 initialData 中没有 openid/nickname 字段，Input 会被强制置空。
           改成纯展示区域后，无论 Form 状态如何都能正确显示。
      */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          marginBottom: 16,
          background: 'var(--td-bg-color-container-hover, #f5f7fa)',
          borderRadius: 4,
        }}
      >
        <Avatar
          size="48px"
          image={getAvatarUrl(initialValue) || undefined}
          icon={<UserIcon />}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
            {getNickname(initialValue) || '（未获取到微信昵称）'}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--td-text-color-secondary, #86909c)',
              wordBreak: 'break-all',
            }}
          >
            OpenID：{initialValue?.openid || '-'}
          </div>
        </div>
      </div>

      {/*
        key 绑定到当前编辑账号 id：每次打开/切换记录，Form 都会以最新 initialData
        重新挂载并渲染出对应的初始值，避免 setFieldsValue 时 ref 尚未挂载导致字段为空。
      */}
      <Form
        key={initialValue?.id ?? 'empty'}
        ref={formRef}
        initialData={toFormValue(initialValue)}
        rules={rules}
        labelWidth={100}
        onSubmit={handleSubmit}
        resetType="initial"
      >
        <FormItem label="姓名" name="name">
          <Input placeholder="请输入姓名" />
        </FormItem>
        <FormItem label="所属单位" name="organization">
          <Input placeholder="请输入所属单位" />
        </FormItem>
        <FormItem label="手机号" name="phone">
          <Input placeholder="请输入手机号（选填）" maxlength={11} />
        </FormItem>
        <FormItem label="状态" name="status">
          {/*
            合并「修改状态」到编辑弹窗：三态互斥单选
             - 待启用（PENDING）：新注册默认态，允许管理员保留
             - 启用（APPROVED）：审批通过 / 恢复启用
             - 禁用（REJECTED）：拒绝 / 停用
            后端 service.update 仅在 status 与当前值不同时才刷新 auditBy/auditAt。
          */}
          <RadioGroup>
            <Radio value={AccountStatus.PENDING}>待启用</Radio>
            <Radio value={AccountStatus.APPROVED}>启用</Radio>
            <Radio value={AccountStatus.REJECTED}>禁用</Radio>
          </RadioGroup>
        </FormItem>
      </Form>
    </Dialog>
  );
};

export default AccountFormDialog;
